/* FC001P-12 Trade Hub navigation behaviour.

   Deliberately not Alpine. The theme's megamenu() component drives the legacy
   header markup and binds its handlers through x-on attributes that this markup
   does not carry, so wiring into it would mean keeping both models in step.

   Desktop: hover or click a top level item to open its panel, mouse out of the
   whole nav to close. Mobile: the toggle opens the drawer, an item with a panel
   slides the drawer to that panel, back returns to the list. */

(() => {
  const DESKTOP = window.matchMedia('(min-width: 990px)');
  const CLOSE_DELAY = 120;

  class TradeHubNav {
    constructor(root) {
      this.root = root;
      this.panels = new Map();
      this.triggers = new Map();
      this.openId = null;
      this.closeTimer = null;
      this.hoverOpened = false;

      root.querySelectorAll('[data-th-nav-panel]').forEach((panel) => {
        this.panels.set(panel.dataset.thNavPanel, panel);
      });

      root.querySelectorAll('[data-th-nav-trigger]').forEach((trigger) => {
        const id = trigger.dataset.thNavTrigger;
        this.triggers.set(id, trigger);

        trigger.addEventListener('click', (event) => {
          event.preventDefault();
          // On desktop the pointer has already opened this panel on mouseenter,
          // so a naive toggle would close it on the very same gesture. The
          // first click after a hover-open is absorbed; the next one closes.
          if (this.openId === id && this.hoverOpened) {
            this.hoverOpened = false;
            return;
          }
          this.toggle(id);
        });

        trigger.addEventListener('mouseenter', () => {
          if (!DESKTOP.matches) return;
          this.cancelClose();
          if (this.openId !== id) this.hoverOpened = true;
          this.open(id);
        });
      });

      root.addEventListener('mouseleave', () => {
        if (!DESKTOP.matches) return;
        this.scheduleClose();
      });

      root.addEventListener('mouseenter', () => this.cancelClose());

      root.querySelectorAll('[data-th-nav-back]').forEach((button) => {
        button.addEventListener('click', () => this.close());
      });

      this.toggleButton = root.querySelector('[data-th-nav-toggle]');
      if (this.toggleButton) {
        this.toggleButton.addEventListener('click', () => this.toggleDrawer());
      }

      this.searchButton = root.querySelector('[data-th-nav-search]');
      this.searchPanel = root.querySelector('[data-th-nav-search-panel]');
      if (this.searchButton && this.searchPanel) {
        this.searchButton.addEventListener('click', () => this.toggleSearch());
      }

      const scrim = root.querySelector('[data-th-nav-scrim]');
      if (scrim) {
        scrim.addEventListener('click', () => this.closeAll());
      }

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') this.closeAll();
      });

      // A resize across the breakpoint leaves the drawer classes stranded.
      DESKTOP.addEventListener('change', () => this.closeAll());

      // Panel images are lazy inside a display:none subtree, which never comes
      // near the viewport, so they only start fetching once the panel opens and
      // the first frame of every panel is grey. Flip them to eager once the page
      // is idle: still nothing on the critical path, but warm before first hover.
      const warm = () => {
        root.querySelectorAll('.th-nav__panel img[loading="lazy"]').forEach((img) => {
          img.loading = 'eager';
        });
      };
      root.addEventListener('pointerenter', warm, { once: true });
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(warm, { timeout: 4000 });
      } else {
        window.setTimeout(warm, 2000);
      }
    }

    open(id) {
      if (!this.panels.has(id)) return;
      if (this.openId === id) return;

      this.closeSearch();
      this.hideCurrent();

      const panel = this.panels.get(id);
      const trigger = this.triggers.get(id);
      panel.hidden = false;
      if (trigger) {
        trigger.setAttribute('aria-expanded', 'true');
        trigger.classList.add('is-active');
      }

      this.openId = id;
      this.root.classList.add('is-open');
      if (!DESKTOP.matches) this.root.classList.add('is-drawer-open');
    }

    close() {
      this.hideCurrent();
      this.openId = null;
      this.hoverOpened = false;
      this.root.classList.remove('is-open');
    }

    hideCurrent() {
      if (this.openId === null) return;
      const panel = this.panels.get(this.openId);
      const trigger = this.triggers.get(this.openId);
      if (panel) panel.hidden = true;
      if (trigger) {
        trigger.setAttribute('aria-expanded', 'false');
        trigger.classList.remove('is-active');
      }
    }

    toggle(id) {
      if (this.openId === id) {
        this.close();
      } else {
        this.open(id);
      }
    }

    scheduleClose() {
      this.cancelClose();
      this.closeTimer = window.setTimeout(() => this.close(), CLOSE_DELAY);
    }

    cancelClose() {
      if (this.closeTimer === null) return;
      window.clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }

    toggleDrawer() {
      const isOpen = this.root.classList.toggle('is-drawer-open');
      this.toggleButton.setAttribute('aria-expanded', String(isOpen));
      document.body.classList.toggle('scroll-disabled', isOpen);
      if (!isOpen) this.close();
    }

    toggleSearch() {
      if (this.searchPanel.hidden) {
        this.close();
        this.searchPanel.hidden = false;
        this.searchButton.setAttribute('aria-expanded', 'true');
        const input = this.searchPanel.querySelector('input[type="search"]');
        if (input) input.focus();
      } else {
        this.closeSearch();
      }
    }

    closeSearch() {
      if (!this.searchPanel || this.searchPanel.hidden) return;
      this.searchPanel.hidden = true;
      this.searchButton.setAttribute('aria-expanded', 'false');
    }

    closeAll() {
      this.close();
      this.closeSearch();
      if (this.root.classList.contains('is-drawer-open')) {
        this.root.classList.remove('is-drawer-open');
        if (this.toggleButton) this.toggleButton.setAttribute('aria-expanded', 'false');
        document.body.classList.remove('scroll-disabled');
      }
    }
  }

  const init = () => {
    document.querySelectorAll('[data-th-nav]').forEach((root) => {
      if (root.dataset.thNavReady === 'true') return;
      root.dataset.thNavReady = 'true';
      new TradeHubNav(root);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme editor re-renders the section without a page load.
  document.addEventListener('shopify:section:load', init);
})();
